import React, { PropsWithChildren, Suspense } from 'react';
import {
  Box,
  Center,
  Checkbox,
  HStack,
  Link,
  LinkBox,
  LinkOverlay,
  SkeletonCircle,
  Stack,
} from '@cardboard-ui/react';
import { Card } from 'components/cards/Card';
import { ItemProps, LoadedItemProps, UploadingItemProps } from './index';
import {
  ItemHandle,
  ItemHandleSkeleton,
  UploadingItemHandle,
} from 'components/ItemHandle';
import { useItem } from 'components/ItemHandle';
import { UploadIcon } from 'components/ItemHandle/components/UploadingHandle';
import { ItemMenu } from './ItemMenu';
import StylishThumbnail from './ItemMenu/components/StylishThumbnail';
import { t } from '@lingui/macro';

export const GridItem = (props: ItemProps) => {
  return (
    <Suspense
      fallback={
        <GridItemSkeleton cover={<SuspendedCover />}>
          <ItemHandleSkeleton />
        </GridItemSkeleton>
      }
    >
      <UnsuspendedGridItem {...props} />
    </Suspense>
  );
};

const SuspendedCover = () => (
  <Center h="100%" w="100%">
    <SkeletonCircle size="20" />
  </Center>
);

export const UnsuspendedGridItem = ({
  node,
  uploadName,
  isLoading,
  loadingPercentage,
  isFailed,
  isVirus,
  ...props
}: ItemProps) => {
  if (isLoading || isFailed) {
    return (
      <UploadingGridItem
        isFailed={isFailed}
        isVirus={isVirus}
        loadingPercentage={loadingPercentage}
        uploadName={uploadName}
        {...props}
      />
    );
  } else {
    return <TrueGridItem node={node} {...props} />;
  }
};

const UploadingGridItem = ({
  isFailed,
  isVirus,
  loadingPercentage,
  uploadName,
  isSelected,
  onSelectChange,
}: UploadingItemProps) => {
  return (
    <Box
      sx={{
        // This is a manual fix of known bug (LinkBox doesn't elevate buttons on top of it) of chakra https://github.com/chakra-ui/chakra-ui/pull/5679
        'a[href]:not(.chakra-linkbox__overlay), abbr[title], button': {
          position: 'relative',
          zIndex: 2,
        },
      }}
    >
      <GridItemSkeleton
        cover={
          <UploadIcon
            loadingPercentage={loadingPercentage}
            isFailed={isFailed}
            isVirus={isVirus}
          />
        }
      >
        <HStack overflow="hidden" flex={1}>
          {onSelectChange && (
            <Checkbox isChecked={isSelected} onChange={onSelectChange} />
          )}
          <UploadingItemHandle
            uploadName={uploadName}
            loadingPercentage={loadingPercentage}
            isFailed={isFailed}
            isVirus={isVirus}
          />
        </HStack>
      </GridItemSkeleton>
    </Box>
  );
};

const TrueGridItem = ({
  node,
  parentConnectionId,
  isSelected,
  onSelectChange,
}: LoadedItemProps) => {
  if (!node) {
    throw new Error('Invalid node');
  }
  const { linkProps } = useItem({ item: node });

  return (
    <LinkBox
      sx={{
        // This is a manual fix of known bug (LinkBox doesn't elevate buttons on top of it) of chakra https://github.com/chakra-ui/chakra-ui/pull/5679
        'a[href]:not(.chakra-linkbox__overlay), abbr[title], button': {
          position: 'relative',
          zIndex: 2,
        },
      }}
    >
      <GridItemSkeleton
        cover={
          <>
            <HStack
              position="absolute"
              justify="space-between"
              top={0}
              left={0}
              right={0}
            >
              {onSelectChange && (
                <Checkbox
                  p={2}
                  isChecked={isSelected}
                  onChange={onSelectChange}
                  zIndex="overlay"
                  sx={{
                    borderColor: 'blackAlpha.500',
                    backgroundColor: 'whiteAlpha.600',
                  }}
                  _dark={{
                    borderColor: 'whiteAlpha.900',
                    backgroundColor: 'blackAlpha.600',
                  }}
                  aria-label={t`Select or unselect`}
                />
              )}

              <HStack justify="end" spacing={2}>
                {node && (
                  <ItemMenu
                    sx={{
                      bg: 'whiteAlpha.600',
                      _dark: {
                        bg: 'blackAlpha.600',
                      },
                    }}
                    node={node}
                    mode="grid-item"
                    connectionId={parentConnectionId}
                  />
                )}
              </HStack>
            </HStack>
            <StylishThumbnail node={node} />
          </>
        }
      >
        <HStack>
          <LinkOverlay as={Link} {...linkProps} overflow="hidden" flex={1}>
            <ItemHandle item={node} />
          </LinkOverlay>
        </HStack>
      </GridItemSkeleton>
    </LinkBox>
  );
};

interface GridItemSkeletonProps {
  cover: React.ReactElement;
}

export const GridItemSkeleton = ({
  cover,
  children,
}: PropsWithChildren<GridItemSkeletonProps>) => {
  return (
    <Card>
      <Stack w="100%" data-qa="item" spacing={0}>
        <Box
          h="150px"
          w="100%"
          bg="gray.300"
          _dark={{ bg: 'gray.600' }}
          position="relative"
        >
          {cover}
        </Box>
        <HStack p={2} w="100%">
          {children}
        </HStack>
      </Stack>
    </Card>
  );
};
